<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { SatProductSummary, useToast } from "@chatfood/design-system";
import { onBeforeMount, computed } from "vue";
import { FetchError } from "ofetch";
import { isBasketRepoError } from "@chatfood/dbe-client/errors";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useOutletStore } from "~/stores/outlet";
import { useBusinessRulesValidation } from "~/composables/use-business-rules-validation";
import { useAnalytics } from "#imports";
import type { IDomainBasketItem } from "~/domain/basket-item";

const HTTP_UNPROCESSABLE_ENTITY = 422;

type ModifierSummary = {
  name: string;
  quantity: number;
  modifiers: ModifierSummary[];
};

const css = bemBuilder("basket-items");
const { t } = useLocale();
const { toastUnexpectedError } = useToast();

const basket = useBasketStore();
const outlet = useOutletStore();

const { resolveBasketException } = useBusinessRulesValidation();

const canMutateBasket = computed(() => basket.isProcessing);

const { trackEvent } = useAnalytics();

const onUpdateItemQuantity = async (
  lineId: string,
  newQuantity: number,
  item: IDomainBasketItem,
) => {
  if (newQuantity === 0) {
    return onRemove(lineId, item);
  }

  await onItemQuantityChange(lineId, newQuantity);

  const quantityDiff = newQuantity - item.quantity;

  trackEvent({
    name: quantityDiff > 0 ? "addItemToBasket" : "removeItemFromBasket",
    payload: {
      id: item.itemId,
      quantity: Math.abs(quantityDiff),
      name: item.name,
      price: item.unitPrice,
    },
  });
};

const onRemove = async (lineId: string, item: IDomainBasketItem) => {
  await onItemQuantityChange(lineId, 0);

  trackEvent({
    name: "removeItemFromBasket",
    payload: {
      id: item.itemId,
      quantity: item.quantity,
      name: item.name,
      price: item.unitPrice,
    },
  });
};

const onItemQuantityChange = async (lineId: string, newQuantity: number) => {
  try {
    await basket.updateItemQuantity(lineId, newQuantity);
  } catch (error: unknown) {
    if (isBasketRepoError(error) && error.isBusinessRule() && error.hasMeta()) {
      const errorData = {
        statusCode: HTTP_UNPROCESSABLE_ENTITY,
        statusMessage: error.meta.violation,
        data: {
          item_id: error.meta.context.itemIds[0],
        },
      };

      return resolveBasketException("updateItemQuantity", errorData);
    }

    if (
      error instanceof FetchError &&
      error.statusCode === HTTP_UNPROCESSABLE_ENTITY &&
      resolveBasketException("updateItemQuantity", error.data)
    ) {
      return;
    }

    toastUnexpectedError();
    throw error;
  }
};

const isItemLoading = (lineId: string) => {
  return basket.processingLineItemId === lineId;
};

const getDiscountedPrice = (item: (typeof basket)["items"][0]) => {
  return item.discountTotal.value ? item.netTotal : undefined;
};

const getModifiers = (
  modifiers: (typeof basket)["items"][0]["modifiers"],
): ModifierSummary[] => {
  if (!modifiers) return [];

  return modifiers.map((modifier) => ({
    name: modifier.name,
    quantity: modifier.quantity,
    modifiers: getModifiers(modifier.modifiers),
  }));
};

const getItemQuantityLimits = (item: IDomainBasketItem) => {
  const itemLimits = basket.itemLimits?.[item.plu];

  if (!itemLimits)
    return {
      maxQuantity: undefined,
      maxQuantityLabel: undefined,
    };

  return {
    maxQuantity: item.quantity + itemLimits.quantity.remaining,
    maxQuantityLabel: t("components.item_builder.max_quantity_is", {
      maxQuantity: itemLimits.quantity.maximum,
    }),
  };
};

onBeforeMount(() => {
  if (!basket.isLoaded || !outlet.isLoaded) {
    throw new Error(
      `BasketItems component requires outlet(${outlet.isLoaded}) and basket(${basket.isLoaded}) store to be loaded.`,
    );
  }
});
</script>

<template>
  <div :class="css('')">
    <SatProductSummary
      v-for="item in basket.items"
      :id="item.lineId"
      :key="item.lineId"
      :class="css('product-line')"
      :quantity="item.quantity"
      :name="item.name"
      counter-size="small"
      :remove-item-label="t('components.basket_items.remove_item')"
      :price="item.grossTotal"
      :is-counter-disabled="canMutateBasket"
      :is-loading="isItemLoading(item.lineId)"
      :discounted-price="getDiscountedPrice(item)"
      :notes="item.notes"
      :notes-label="t('components.basket_items.item_notes_label')"
      :modifiers="getModifiers(item.modifiers)"
      :total="item.grossTotal"
      :is-view-only="false"
      :max-item-quantity="getItemQuantityLimits(item).maxQuantity"
      :max-quantity-label="getItemQuantityLimits(item).maxQuantityLabel"
      @change="
        (lineId: string, newQuantity: number) =>
          onUpdateItemQuantity(lineId, newQuantity, item)
      "
      @clear="(lineId: string) => onRemove(lineId, item)"
    />
  </div>
</template>

<style lang="scss" scoped>
.basket-items {
  &__product-line {
    padding: var(--spacing-md) 0;
    border-bottom: 1px solid var(--color-base-95);

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
      border: none;
    }
  }
}
</style>
